// frontend/src/pages/Staff.tsx

import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getStaff,
  createStaff,
  updateStaff,
  deleteStaff,
} from "../api/staff";
import {
  Staff,
  CreateStaffRequest,
  UpdateStaffRequest,
} from "../types/staff";
import Sidebar from "../components/Sidebar";
import StaffForm from "../components/StaffForm";
import EditStaffForm from "../components/EditStaffForm";
import {
  Plus,
  Download,
  Search,
  Edit3,
  Trash2,
  UsersRound,
  Mail,
  MapPin,
} from "lucide-react";

export default function StaffPage() {
  const { user } = useContext(AuthContext);
  const [list, setList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<Staff | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      const data = await getStaff();

      if (mounted) {
        setList(data);
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (!user) return <Navigate to="/" replace />;

  const canEdit = user.role !== "viewer";
  const canDelete = user.role === "superadmin";

  const handleCreate = async (d: CreateStaffRequest) => {
    const s = await createStaff(d);
    setList((prev) => [s, ...prev]);
    setShowNew(false);
  };

  const handleUpdate = async (
    id: string,
    d: UpdateStaffRequest,
  ) => {
    const s = await updateStaff(id, d);

    setList((prev) =>
      prev.map((x) => (x.id === id ? s : x)),
    );

    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this staff member?")) return;

    await deleteStaff(id);
    setList((prev) => prev.filter((x) => x.id !== id));
  };

  // filter by name/email/address
  const filteredStaff = list.filter(
    (staff) =>
      staff.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      staff.email
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      staff.address
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  // initials helper
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // CSV export logic
  const csvEscape = (v: string | number) => {
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const handleExport = () => {
    const headers = [
      "Name",
      "Email",
      "Address",
      "Salary",
      "JoinedDate",
      "CreatedBy",
      "PAN",
    ];

    const rows = list.map((s) => [
      s.name,
      s.email,
      s.address,
      s.salary,
      s.joinedDate,
      s.createdBy?.username || s.createdByName || "",
      s.panNumber,
    ]);

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "staff_export.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  const actionButtonClass = `
    inline-flex h-8 w-8 shrink-0 items-center justify-center
    rounded-lg border bg-white p-0 transition
    focus:outline-none focus:ring-2
  `;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
          ml-[280px] min-w-0 px-6 py-6
          transition-[margin] duration-200
          [.sidebar-collapsed_&]:ml-[76px]
          max-lg:ml-[76px]
          max-lg:px-4
          max-sm:px-3
        "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* ============================================================
              HEADER
          ============================================================ */}
          <header className="mb-5 flex items-start justify-between gap-4 max-md:flex-col max-md:items-stretch">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="
                  grid h-10 w-10 shrink-0 place-items-center
                  rounded-[10px] bg-slate-100 text-slate-600
                "
              >
                <UsersRound size={20} strokeWidth={1.8} />
              </div>

              <div className="min-w-0">
                <div
                  className="
                    mb-1 text-[10px] font-bold uppercase
                    tracking-[0.12em] text-blue-600
                  "
                >
                  Staff management
                </div>

                <h1
                  className="
                    m-0 text-[28px] font-bold
                    tracking-[-0.03em] text-slate-950
                    max-sm:text-[24px]
                  "
                >
                  Staff
                </h1>

                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Manage staff members, salary details and records.
                </p>
              </div>
            </div>

            {canEdit && (
              <div className="flex shrink-0 items-center gap-2 max-sm:grid max-sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleExport}
                  className="
                    inline-flex h-10 items-center justify-center gap-2
                    rounded-[9px]
                    border border-emerald-200
                    bg-white px-4
                    text-[11px] font-semibold text-emerald-700
                    transition
                    hover:border-emerald-300
                    hover:bg-emerald-50
                    hover:text-emerald-800
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <Download size={15} />
                  Export
                </button>

                <button
                  type="button"
                  onClick={() => setShowNew(true)}
                  className="
                    inline-flex h-10 items-center justify-center gap-2
                    rounded-[9px]
                    border border-emerald-600
                    bg-emerald-600 px-4
                    text-[11px] font-semibold text-white
                    transition
                    hover:border-emerald-700
                    hover:bg-emerald-700
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <Plus size={15} />
                  New Staff
                </button>
              </div>
            )}
          </header>

          {/* ============================================================
              SEARCH
          ============================================================ */}
          <section
            className="
              mb-4 rounded-[16px]
              border border-slate-200 bg-white
              p-4
              shadow-[0_1px_3px_rgba(15,23,42,0.04)]
            "
          >
            <label className="block max-w-[620px]">
              <span
                className="
                  mb-1.5 block
                  text-[10px] font-semibold uppercase
                  tracking-[0.04em] text-slate-400
                "
              >
                Search staff
              </span>

              <div className="relative">
                <Search
                  size={16}
                  className="
                    pointer-events-none absolute left-3 top-1/2
                    -translate-y-1/2 text-slate-400
                  "
                />

                <input
                  type="text"
                  placeholder="Search by name, email or address"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="
                    box-border h-10 w-full rounded-[9px]
                    border border-slate-200 bg-white
                    pl-9 pr-3
                    text-[12px] font-medium text-slate-800
                    outline-none transition
                    placeholder:text-slate-400
                    hover:border-slate-300
                    focus:border-slate-400
                    focus:ring-2 focus:ring-slate-100
                  "
                />
              </div>
            </label>
          </section>

          {/* ============================================================
              LOADING / TABLE
          ============================================================ */}
          {loading ? (
            <section
              className="
                flex min-h-[260px] flex-col items-center justify-center
                rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <span
                className="
                  h-6 w-6 animate-spin rounded-full
                  border-[3px] border-slate-200 border-t-slate-700
                "
              />

              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading staff…
              </span>
            </section>
          ) : (
            <section
              className="
                overflow-hidden rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <div
                className="
                  flex items-center justify-between gap-4
                  border-b border-slate-100
                  px-5 py-4
                  max-sm:flex-col max-sm:items-start max-sm:px-4
                "
              >
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Staff Members
                  </h2>

                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filteredStaff.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {list.length}
                    </strong>{" "}
                    staff member{list.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
                  <strong className="font-semibold text-slate-800">
                    {filteredStaff.length}
                  </strong>{" "}
                  shown
                </div>
              </div>

              {filteredStaff.length > 0 ? (
                <div className="w-full overflow-x-auto">
                  <table className="w-full min-w-[1080px] border-collapse">
                    <thead className="bg-slate-50/80">
                      <tr>
                        {[
                          "Staff Member",
                          "Address",
                          "Salary",
                          "Joined",
                          "PAN",
                        ].map((label) => (
                          <th
                            key={label}
                            className="
                              px-4 py-3.5 text-left
                              text-[14px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            {label}
                          </th>
                        ))}

                        {canEdit && (
                          <th
                            className="
                              w-[110px] px-4 py-3.5 text-center
                              text-[14px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {filteredStaff.map((staff) => (
                        <tr
                          key={staff.id}
                          className="
                            border-b border-slate-100
                            transition-colors
                            last:border-b-0
                            hover:bg-slate-50/60
                          "
                        >
                          {/* Staff */}
                          <td className="px-4 py-4 align-middle">
                            <div className="flex min-w-[230px] items-center gap-3">
                              <div
                                className="
                                  grid h-9 w-9 shrink-0 place-items-center
                                  rounded-full
                                  bg-slate-100
                                  text-[11px] font-bold text-slate-600
                                "
                              >
                                {getInitials(staff.name)}
                              </div>

                              <div className="min-w-0">
                                <div
                                  className="
                                    max-w-[230px] truncate
                                    text-[15px] font-semibold text-slate-950
                                  "
                                  title={staff.name}
                                >
                                  {staff.name}
                                </div>

                                <div
                                  className="
                                    mt-1 flex max-w-[250px]
                                    items-center gap-1.5
                                    text-[13px] text-slate-500
                                  "
                                >
                                  <Mail
                                    size={13}
                                    className="shrink-0 text-slate-400"
                                  />

                                  <span className="truncate" title={staff.email}>
                                    {staff.email}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Address */}
                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                flex min-w-[180px] max-w-[260px]
                                items-start gap-1.5
                                text-[13px] leading-5 text-slate-600
                              "
                            >
                              <MapPin
                                size={13}
                                className="mt-0.5 shrink-0 text-slate-400"
                              />

                              <span className="break-words">
                                {staff.address || "—"}
                              </span>
                            </div>
                          </td>

                          {/* Salary */}
                          <td className="px-4 py-4 align-middle">
                            <div className="min-w-[120px] text-[14px] font-semibold text-slate-900">
                              {"Rs " +
                                new Intl.NumberFormat("en-NP").format(
                                  staff.salary,
                                )}
                            </div>
                          </td>

                          {/* Joined */}
                          <td className="px-4 py-4 align-middle">
                            <div className="min-w-[140px]">
                              <div className="text-[14px] font-medium text-slate-700">
                                {new Date(
                                  staff.joinedDate,
                                ).toLocaleDateString()}
                              </div>

                              <div
                                className="
                                  mt-1 max-w-[170px] truncate
                                  text-[12px] text-slate-500
                                "
                                title={
                                  staff.createdBy?.username ??
                                  staff.createdByName ??
                                  undefined
                                }
                              >
                                {staff.createdBy?.username ??
                                  staff.createdByName ??
                                  "—"}
                              </div>
                            </div>
                          </td>

                          {/* PAN */}
                          <td className="px-4 py-4 align-middle">
                            <span
                              className="
                                inline-flex rounded-full
                                bg-slate-100 px-2.5 py-1
                                text-[12px] font-semibold text-slate-700
                              "
                            >
                              {staff.panNumber || "—"}
                            </span>
                          </td>

                          {/* Actions */}
                          {canEdit && (
                            <td className="px-4 py-4 text-center align-middle">
                              <div className="inline-flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setEditTarget(staff)}
                                  title="Edit Staff"
                                  aria-label={`Edit ${staff.name}`}
                                  className={`
                                    ${actionButtonClass}
                                    border-emerald-200
                                    text-emerald-700
                                    hover:border-emerald-300
                                    hover:bg-emerald-50
                                    hover:text-emerald-800
                                    focus:ring-emerald-200
                                  `}
                                >
                                  <Edit3 size={15} />
                                </button>

                                {canDelete && (
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(staff.id)}
                                    title="Delete Staff"
                                    aria-label={`Delete ${staff.name}`}
                                    className={`
                                      ${actionButtonClass}
                                      border-red-200
                                      text-red-600
                                      hover:border-red-300
                                      hover:bg-red-50
                                      hover:text-red-700
                                      focus:ring-red-200
                                    `}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="
                    flex min-h-[230px] flex-col
                    items-center justify-center px-6 text-center
                  "
                >
                  <div
                    className="
                      grid h-11 w-11 place-items-center
                      rounded-xl bg-slate-100 text-slate-400
                    "
                  >
                    <UsersRound size={20} />
                  </div>

                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No staff members found
                  </h3>

                  <p
                    className="
                      mb-0 mt-1 max-w-[430px]
                      text-[10px] leading-5 text-slate-500
                    "
                  >
                    {searchTerm
                      ? "Try changing your search to see more staff members."
                      : "No staff members are available to display."}
                  </p>
                </div>
              )}
            </section>
          )}

          {/* Modals */}
          {showNew && (
            <StaffForm
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {editTarget && (
            <EditStaffForm
              staff={editTarget}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
